import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { v4 as uuid } from 'uuid';
import startCase from 'lodash.startcase';
import { useTranslation } from 'react-i18next';
import Table from '../../Table/Table';
import TableHead from '../../Table/TableHead';
import TableBody from '../../Table/TableBody';
import TableRow from '../../Table/TableRow';
import { formatDate } from '../../../util/helpers/dateHelpers';
import {
  transactionRequiestStatusTypesArray,
  triggerOrderStatusTypesArray,
  TRANSACTION_REQUEST_TYPE_TRIGGER_ORDER,
} from '../../../util/enum/api/transactionTypes';
import { productLocationTypes } from '../../../util/enum/api/productTypes';
import { parseEnumType } from '../../../util/helpers/enumMappers';
import { selectIsLoadingByActionType } from '../../../store/selectors/loadingSelectors';
import { selectSettings } from '../../../store/selectors/settingsSelectors';
import { GET_TRIGGER_ORDER_DETAILS_LOADING } from '../../../store/actions/triggerOrders/triggerOrdersActionConstants';
import { getTriggerOrderDetails } from '../../../store/actions/triggerOrders/triggerOrdersActions';
import { mergeTriggerOrderDetails } from '../../../util/helpers/transcation/singleTransactionHelper';
import SectionLoader from '../../Loader/SectionLoader';
import PendingTransactionRow from './PendingTransactionRow';
import TriggerOrderDetailsModal from '../../Modals/TriggerOrderDetails/TriggerOrderDetailsModal';
import {
  pendingTransactionHead,
  pendingTransactionRow,
  pendingTransactionStyle,
} from '../../Table/Components/PendingTransaction';
import {
  HoldingTableEmptyStyle,
  HoldingTableEmptyTextStyle,
} from '../../Table/Components/HoldingTable';

const PendingTransactionsTable = ({
  data,
  accountUid,
  shouldShowTransactions,
  handleModalOpen,
  handleCancelTriggerOrderModalOpen,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { SegregatedCaption } = useSelector(selectSettings);
  const isOrderDetailsLoading = useSelector(
    selectIsLoadingByActionType(GET_TRIGGER_ORDER_DETAILS_LOADING),
  );

  const [rowData, setRowData] = useState([]);
  const [isTriggerOrderDetailsOpen, setIsTriggerOrderDetailsOpen] = useState(
    false,
  );
  const [selectedTriggerOrder, setSelectedTriggerOrder] = useState(null);

  const showTriggerOrderDetails = (transaction) => {
    setSelectedTriggerOrder(transaction);
    setIsTriggerOrderDetailsOpen(true);
  };

  // An Active Target Price Order's details come from the same action the Buy at
  // Target Price flow's View Order uses (getTriggerOrderDetails: the single
  // transaction API with the order's TriggerOrderUid); the modal opens once they
  // have loaded, with the table's loader showing meanwhile. The row's own fields
  // win over the response (mergeTriggerOrderDetails), and if the call fails (a
  // 500 included: it opts out of the error page redirect) the modal still opens
  // with the row's data. Other statuses open straight away.
  const handleTriggerOrderDetailsOpen = (transaction) => {
    const status = parseEnumType(
      triggerOrderStatusTypesArray,
      transaction.TriggerOrderStatus,
    );

    if (status !== 'Active' || !transaction.TriggerOrderUid) {
      showTriggerOrderDetails(transaction);
      return;
    }

    dispatch(
      getTriggerOrderDetails({
        accountUid,
        triggerOrderUid: transaction.TriggerOrderUid,
        onSuccess: (details) =>
          showTriggerOrderDetails(
            mergeTriggerOrderDetails(details, transaction),
          ),
        onError: () => showTriggerOrderDetails(transaction),
      }),
    );
  };

  const handleTriggerOrderDetailsClose = () => {
    setIsTriggerOrderDetailsOpen(false);
  };

  const headerData = [
    {
      id: 1,
      content: t(
        'portfolio.pendingTransactions.tableHeadings.yourTransactions',
      ),
    },
    { id: 2, content: t('portfolio.pendingTransactions.tableHeadings.amount') },
    { id: 3, content: t('portfolio.pendingTransactions.tableHeadings.status') },
    { id: 4, content: '' },
  ];

  const tableBodyData = data && data?.length > 0 ? data : []; //eslint-disable-line

  useEffect(() => {
    if (tableBodyData && tableBodyData.length) {
      setRowData(
        tableBodyData.map((transaction) => {
          if (
            transaction.TransactionRequestType ===
            TRANSACTION_REQUEST_TYPE_TRIGGER_ORDER
          ) {
            const unit = t(
              transaction.Quantity > 1
                ? 'portfolio.transactions.details.pluralUnit'
                : 'portfolio.transactions.details.singleUnit',
            );

            return {
              id: transaction.TriggerOrderUid,
              content: (
                <PendingTransactionRow
                  key={`${transaction.TriggerOrderUid}-${uuid()}`}
                  date={formatDate(transaction.RequestDate, 'MMM dd')}
                  amount={transaction.Amount}
                  amountCaption={t('balances.reservedBalanceLabel')}
                  label={t('portfolio.pendingTransactions.triggerOrderLabel', {
                    quantity: transaction.Quantity,
                    unit,
                  })}
                  product={transaction.ProductCaption}
                  isSegregated={transaction.IsSegregated}
                  segregatedCaption={SegregatedCaption}
                  vaultName={startCase(
                    parseEnumType(
                      productLocationTypes,
                      transaction.LocationType,
                    ),
                  )}
                  status={parseEnumType(
                    triggerOrderStatusTypesArray,
                    transaction.TriggerOrderStatus,
                  )}
                  canCancel={transaction.CanCancel}
                  handleModalOpen={() =>
                    handleCancelTriggerOrderModalOpen(
                      accountUid,
                      transaction.TriggerOrderUid,
                      transaction.Amount,
                      transaction,
                    )
                  }
                  isClickable
                  isTriggerOrder
                />
              ),
              // Opens the order's details; TableRow runs it for a click
              // anywhere on the row (the Cancel button stops its own).
              onRowClick: () => handleTriggerOrderDetailsOpen(transaction),
            };
          }

          return {
            id: transaction.DepositKey,
            content: (
              <PendingTransactionRow
                key={`${transaction.DepositKey}-${uuid()}`}
                date={formatDate(transaction.RequestDate, 'MMM dd')}
                amount={transaction.Amount}
                bankAccountNumber={transaction.BankAccountNumber}
                status={parseEnumType(
                  transactionRequiestStatusTypesArray,
                  transaction.RequestStatus,
                )}
                canCancel={transaction.CanCancel}
                action={transaction.TransactionType}
                handleModalOpen={() =>
                  handleModalOpen(
                    accountUid,
                    transaction.DepositKey,
                    transaction.Amount,
                  )
                }
              />
            ),
          };
        }),
      );
    }
  }, [data]); //eslint-disable-line

  return (
    <>
      <Table css={pendingTransactionStyle}>
        <TableHead data={headerData} css={pendingTransactionHead} />
        <SectionLoader isLoading={isOrderDetailsLoading}>
          {shouldShowTransactions ? (
            <TableBody>
              <TableRow
                key={uuid()}
                css={pendingTransactionRow}
                rowData={rowData}
                onClick={(row) => row.onRowClick?.()}
                data-cy="list-item-transaction-request"
              />
            </TableBody>
          ) : (
            <HoldingTableEmptyStyle isSmall>
              <HoldingTableEmptyTextStyle>
                {t('balances.noRecentTransactions')}
              </HoldingTableEmptyTextStyle>
            </HoldingTableEmptyStyle>
          )}
        </SectionLoader>
      </Table>
      <TriggerOrderDetailsModal
        isOpen={isTriggerOrderDetailsOpen}
        onClose={handleTriggerOrderDetailsClose}
        transaction={selectedTriggerOrder}
      />
    </>
  );
};

PendingTransactionsTable.propTypes = {
  data: PropTypes.oneOfType([
    PropTypes.arrayOf(
      PropTypes.shape({
        DateTime: PropTypes.string,
        Amount: PropTypes.number,
        EffectiveBalance: PropTypes.number,
        StatusType: PropTypes.number,
        Caption: PropTypes.string,
      }),
    ),
    PropTypes.shape({}),
  ]),
  accountUid: PropTypes.string,
  shouldShowTransactions: PropTypes.bool,
  handleModalOpen: PropTypes.func,
  handleCancelTriggerOrderModalOpen: PropTypes.func,
};

export default PendingTransactionsTable;
